import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  UserMinus, AlertTriangle, Check, X, Clock, DollarSign,
  Loader2, Settings, Info, ChevronRight
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { toast } from 'sonner';

const statusConfig = {
  pending: { label: 'Pendente', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400', icon: Clock },
  approved: { label: 'Aprovada', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', icon: Check },
  rejected: { label: 'Recusada', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400', icon: X },
};

const refundTypeLabels = {
  full: 'Reembolso Total',
  partial: 'Reembolso Parcial',
  none: 'Sem Reembolso',
};

export default function TripWithdrawals() {
  const { tripId } = useParams();
  const context = useOutletContext();
  const { isOrganizer, trip } = context || {};
  const { api, user } = useAuth();
  
  const [withdrawals, setWithdrawals] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  
  const [requestReason, setRequestReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [editingPolicy, setEditingPolicy] = useState(null);

  useEffect(() => {
    fetchData();
  }, [tripId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [withdrawalsRes, policyRes] = await Promise.all([
        api.get(`/trips/${tripId}/withdrawals`),
        api.get(`/trips/${tripId}/withdrawals/policy`)
      ]);
      setWithdrawals(withdrawalsRes.data);
      setPolicy(policyRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    setSubmitting(true);
    try {
      const response = await api.post(`/trips/${tripId}/withdrawals/preview`);
      setPreview(response.data);
      setShowPreviewDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao calcular prévia');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestWithdrawal = async () => {
    setSubmitting(true);
    try {
      await api.post(`/trips/${tripId}/withdrawals`, { reason: requestReason });
      toast.success('Solicitação enviada! O organizador será notificado.');
      setShowRequestDialog(false);
      setShowPreviewDialog(false);
      setRequestReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar solicitação');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (withdrawalId) => {
    setSubmitting(true);
    try {
      const response = await api.post(`/trips/${tripId}/withdrawals/${withdrawalId}/approve`);
      toast.success(`Desistência aprovada! ${response.data.remaining_members} membros restantes.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao aprovar');
    } finally {
      setSubmitting(false);
      setShowConfirmDialog(false);
    }
  };

  const handleReject = async (withdrawalId) => {
    setSubmitting(true);
    try {
      await api.post(`/trips/${tripId}/withdrawals/${withdrawalId}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success('Desistência recusada');
      setRejectReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao recusar');
    } finally {
      setSubmitting(false);
      setShowConfirmDialog(false);
    }
  };

  const handleCancelRequest = async (withdrawalId) => {
    try {
      await api.delete(`/trips/${tripId}/withdrawals/${withdrawalId}`);
      toast.success('Solicitação cancelada');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  const handleUpdatePolicy = async () => {
    if (!editingPolicy) return;
    setSubmitting(true);
    try {
      await api.put(`/trips/${tripId}/withdrawals/policy`, editingPolicy);
      toast.success('Política atualizada!');
      setPolicy(editingPolicy);
      setShowPolicyDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar política');
    } finally {
      setSubmitting(false);
    }
  };

  const myPendingRequest = withdrawals.find(
    w => w.user_id === user?.id && w.status === 'pending'
  );

  const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending');
  const processedWithdrawals = withdrawals.filter(w => w.status !== 'pending');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-xl text-slate-900 dark:text-white">Desistências</h2>
          <p className="text-slate-500 dark:text-slate-400">Gerenciar saídas e reembolsos</p>
        </div>
        <div className="flex gap-2">
          {isOrganizer && (
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => {
                setEditingPolicy({ ...policy });
                setShowPolicyDialog(true);
              }}
              data-testid="edit-policy-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Política
            </Button>
          )}
          {!isOrganizer && !myPendingRequest && (
            <Button
              className="rounded-full bg-red-500 hover:bg-red-600"
              onClick={handlePreview}
              disabled={submitting}
              data-testid="request-withdrawal-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <UserMinus className="w-4 h-4 mr-2" />
                  Desistir
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Policy Info Card */}
      {policy && (
        <Card className="border-slate-100 bg-slate-50 dark:bg-slate-800">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <h4 className="font-medium text-slate-900 dark:text-white mb-1">Política de Reembolso</h4>
                <div className="text-sm text-slate-600 space-y-1">
                  {policy.rules.map((rule, idx) => (
                    <p key={idx}>
                      • {rule.days_before > 0 ? `Até ${rule.days_before} dias antes` : 'Menos de 7 dias'}: {rule.refund_percentage}% de reembolso
                    </p>
                  ))}
                </div>
                {policy.custom_message && (
                  <p className="mt-2 text-sm text-slate-500 italic">{policy.custom_message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* My Pending Request */}
      {myPendingRequest && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-yellow-200 bg-yellow-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-700">
                <Clock className="w-5 h-5" />
                Sua solicitação pendente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Valor pago</p>
                  <p className="font-bold text-lg">R$ {myPendingRequest.total_paid.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Reembolso previsto</p>
                  <p className="font-bold text-lg text-green-600">R$ {myPendingRequest.refund_amount.toFixed(2)}</p>
                </div>
              </div>
              {myPendingRequest.reason && (
                <p className="text-sm text-slate-600 mb-4">
                  <span className="font-medium">Motivo:</span> {myPendingRequest.reason}
                </p>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCancelRequest(myPendingRequest.id)}
                className="text-red-600 hover:text-red-700"
              >
                Cancelar solicitação
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Pending Withdrawals (Organizer) */}
      {isOrganizer && pendingWithdrawals.length > 0 && (
        <div>
          <h3 className="font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-yellow-500" />
            Solicitações Pendentes ({pendingWithdrawals.length})
          </h3>
          <div className="space-y-3">
            {pendingWithdrawals.map((withdrawal) => (
              <motion.div
                key={withdrawal.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Card className="border-yellow-200">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{withdrawal.user_name}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          Solicitado em {format(parseISO(withdrawal.requested_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                        </p>
                        {withdrawal.reason && (
                          <p className="text-sm text-slate-600 mt-1">
                            <span className="font-medium">Motivo:</span> {withdrawal.reason}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500 dark:text-slate-400">Reembolso</p>
                        <p className="font-bold text-green-600">
                          R$ {withdrawal.refund_amount.toFixed(2)}
                          <span className="text-xs text-slate-400 ml-1">
                            ({withdrawal.refund_percentage}%)
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button
                        size="sm"
                        className="flex-1 bg-green-500 hover:bg-green-600 rounded-full"
                        onClick={() => {
                          setSelectedWithdrawal(withdrawal);
                          setConfirmAction('approve');
                          setShowConfirmDialog(true);
                        }}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Aprovar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-red-600 hover:text-red-700 rounded-full"
                        onClick={() => {
                          setSelectedWithdrawal(withdrawal);
                          setConfirmAction('reject');
                          setShowConfirmDialog(true);
                        }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Recusar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Processed Withdrawals */}
      {processedWithdrawals.length > 0 && (
        <div>
          <h3 className="font-semibold text-slate-700 dark:text-slate-300 mb-3">Histórico</h3>
          <div className="space-y-2">
            {processedWithdrawals.map((withdrawal) => {
              const config = statusConfig[withdrawal.status];
              const StatusIcon = config.icon;
              return (
                <Card key={withdrawal.id} className="border-slate-100 dark:border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full ${config.color} flex items-center justify-center`}>
                          <StatusIcon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{withdrawal.user_name}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {format(parseISO(withdrawal.processed_at || withdrawal.requested_at), "dd/MM/yyyy", { locale: ptBR })}
                          </p>
                        </div>
                      </div>
                      <Badge className={config.color}>{config.label}</Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && withdrawals.length === 0 && !myPendingRequest && (
        <Card className="border-slate-100 dark:border-slate-700 border-dashed">
          <CardContent className="py-12 text-center">
            <UserMinus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-2">
              Nenhuma desistência
            </h3>
            <p className="text-slate-500 dark:text-slate-400">
              {isOrganizer 
                ? 'Ainda não há solicitações de desistência'
                : 'Se precisar sair da viagem, clique no botão Desistir'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Prévia da Desistência</DialogTitle>
            <DialogDescription>
              Veja o que aconteceria se você desistisse agora
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500 dark:text-slate-400">Você pagou</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-white">R$ {preview.total_paid.toFixed(2)}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600">Reembolso</p>
                  <p className="text-xl font-bold text-green-600">R$ {preview.refund_amount.toFixed(2)}</p>
                </div>
              </div>
              
              <div className="p-3 bg-orange-50 rounded-lg">
                <p className="text-sm text-orange-600">Você perderia</p>
                <p className="text-xl font-bold text-orange-600">R$ {preview.amount_lost.toFixed(2)}</p>
              </div>

              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <Badge variant="outline">{refundTypeLabels[preview.refund_type]}</Badge>
                <span>({preview.refund_percentage}%)</span>
              </div>

              {preview.policy_message && (
                <p className="text-sm text-slate-500 italic">{preview.policy_message}</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-red-500 hover:bg-red-600"
              onClick={() => setShowRequestDialog(true)}
            >
              Confirmar Desistência
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Desistência</DialogTitle>
            <DialogDescription>
              Sua solicitação será enviada para aprovação do organizador
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Motivo (opcional)</Label>
            <Textarea
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              placeholder="Explique brevemente o motivo da desistência..."
              className="mt-1"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>
              Voltar
            </Button>
            <Button
              className="bg-red-500 hover:bg-red-600"
              onClick={handleRequestWithdrawal}
              disabled={submitting}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar Solicitação'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Policy Edit Dialog */}
      <Dialog open={showPolicyDialog} onOpenChange={setShowPolicyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Política de Reembolso</DialogTitle>
            <DialogDescription>
              Configure as regras de reembolso para desistências
            </DialogDescription>
          </DialogHeader>
          {editingPolicy && (
            <div className="space-y-4 py-4">
              {editingPolicy.rules.map((rule, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="flex-1">
                    <Label className="text-xs">Dias antes</Label>
                    <Input
                      type="number"
                      value={rule.days_before}
                      onChange={(e) => {
                        const newRules = [...editingPolicy.rules];
                        newRules[idx].days_before = parseInt(e.target.value) || 0;
                        setEditingPolicy({ ...editingPolicy, rules: newRules });
                      }}
                      className="h-9"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Reembolso %</Label>
                    <Input
                      type="number"
                      value={rule.refund_percentage}
                      onChange={(e) => {
                        const newRules = [...editingPolicy.rules];
                        newRules[idx].refund_percentage = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                        setEditingPolicy({ ...editingPolicy, rules: newRules });
                      }}
                      className="h-9"
                    />
                  </div>
                </div>
              ))}
              
              <div>
                <Label>Mensagem personalizada (opcional)</Label>
                <Textarea
                  value={editingPolicy.custom_message || ''}
                  onChange={(e) => setEditingPolicy({ ...editingPolicy, custom_message: e.target.value })}
                  placeholder="Ex: Entre em contato com o organizador para casos especiais..."
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPolicyDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdatePolicy} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Action Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === 'approve' ? 'Aprovar Desistência?' : 'Recusar Desistência?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === 'approve' ? (
                <>
                  Ao aprovar, {selectedWithdrawal?.user_name} será removido da viagem e 
                  receberá R$ {selectedWithdrawal?.refund_amount.toFixed(2)} de reembolso.
                  Os pagamentos serão recalculados para os membros restantes.
                </>
              ) : (
                <div className="space-y-3">
                  <p>O membro continuará na viagem.</p>
                  <div>
                    <Label>Motivo da recusa (opcional)</Label>
                    <Textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Explique o motivo..."
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmAction === 'approve') {
                  handleApprove(selectedWithdrawal?.id);
                } else {
                  handleReject(selectedWithdrawal?.id);
                }
              }}
              className={confirmAction === 'approve' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'}
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
